import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Wrench, Check } from 'lucide-react';
import { toast } from 'sonner';
import useEquipmentCatalog from '../../hooks/useEquipmentCatalog';
import EquipmentFields, { equipmentSummary } from './EquipmentFields';
import { recordSite, recordSitePatch } from '../../utils/siteLocation';
import { UnitAttachments } from '../customer/UnitAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JobEquipmentSection({ jobId, job, equipment, canModify, onReload }) {
  const catalog = useEquipmentCatalog();
  const types = catalog.types;
  const [items, setItems] = useState(equipment || []);
  const [saving, setSaving] = useState(false);
  const [locationReuse, setLocationReuse] = useState(null);

  useEffect(() => { setItems(equipment || []); setLocationReuse(null); }, [equipment]);

  const dirty = !!locationReuse || JSON.stringify(items) !== JSON.stringify(equipment || []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { equipment: items,
        ...(locationReuse ? recordSitePatch(locationReuse.site_details, true) : {}),
        ...(locationReuse?.customer_id && !job?.customer_id ? { customer_id: locationReuse.customer_id, customer_name: locationReuse.customer_name } : {}) });
      toast.success('Customer assets saved');
      await catalog.refresh();
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save customer assets');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="job-equipment-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Wrench size={18} className="text-slate-400" /> Customer Assets
        </h3>
        {canModify && dirty && (
          <Button size="sm" onClick={save} disabled={saving} className="bg-slate-900" data-testid="job-equipment-save-btn">
            <Check size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save Assets'}
          </Button>
        )}
      </div>

      {canModify ? (
        <><EquipmentFields value={items} onChange={setItems} types={types} testPrefix="job-detail-equipment"
          customerId={job?.customer_id || locationReuse?.customer_id || null} site={locationReuse?.site_details || recordSite(job || {}, [], true)}
          onReuseSite={(site, row) => setLocationReuse({ ...row, site_details: site })} />
          {locationReuse && <p className="mt-3 text-sm text-slate-600 break-words" data-testid="job-equipment-pending-location">Selected location: {[locationReuse.customer_name, locationReuse.site_details.name, locationReuse.site_details.lsd || locationReuse.site_details.address].filter(Boolean).join(' · ')}</p>}</>
      ) : items.length ? (
        <ul className="space-y-1.5" data-testid="job-equipment-readonly">
          {items.map((it) => (
            <li key={it.id} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <p className="font-medium text-slate-800">{equipmentSummary(it, catalog.allTypes)}</p>
              {it.notes && <p className="mt-0.5 text-slate-600 whitespace-pre-wrap break-words">{it.notes}</p>}
              <UnitAttachments ids={it.attachment_ids} prefix={`job-unit-files-${it.id}`} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No customer assets recorded on this job.</p>
      )}
    </div>
  );
}
